import { useEffect, useState } from "react";
import { Copy, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Settings() {
  const [rooms, setRooms] = useState([]);
  const [packages, setPackages] = useState([]);
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/botbee`;
  const razorpayWebhook = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/razorpay`;

  useEffect(() => {
    api.get("/rooms").then(r => setRooms(r.data));
    api.get("/packages").then(r => setPackages(r.data));
  }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-4xl">Settings</h1>
        <p className="text-[#8A7970] mt-1">Integration endpoints & configuration.</p>
      </div>

      <div className="max-w-3xl space-y-6">
        <section className="bg-[#FDFBF7] rounded-xl border border-[#E7C2A6]/40 p-6">
          <h2 className="font-serif text-2xl mb-3">Botbee.io Webhook</h2>
          <p className="text-sm text-[#8A7970] mb-4">Paste this URL inside Botbee → Settings → Webhooks. Set method to POST, JSON body. When a guest messages your WhatsApp, Botbee will forward it here and our AI will reply.</p>
          <div className="flex items-center gap-2 bg-[#F5F2EB] p-3 rounded-lg font-mono text-xs">
            <span className="flex-1 truncate">{webhookUrl}</span>
            <Button size="sm" variant="outline" onClick={() => copy(webhookUrl)} data-testid="copy-botbee-webhook"><Copy className="w-3 h-3"/></Button>
          </div>
          <div className="mt-3 text-xs text-[#8A7970]">
            Optional header for security: <code className="bg-[#F5F2EB] px-1.5 py-0.5 rounded">X-Botbee-Secret: mb_botbee_secret_2026</code>
          </div>
        </section>

        <section className="bg-[#FDFBF7] rounded-xl border border-[#E7C2A6]/40 p-6">
          <h2 className="font-serif text-2xl mb-3">Razorpay Webhook</h2>
          <p className="text-sm text-[#8A7970] mb-4">Add this URL in Razorpay Dashboard → Settings → Webhooks. Subscribes to <code>payment.captured</code>.</p>
          <div className="flex items-center gap-2 bg-[#F5F2EB] p-3 rounded-lg font-mono text-xs">
            <span className="flex-1 truncate">{razorpayWebhook}</span>
            <Button size="sm" variant="outline" onClick={() => copy(razorpayWebhook)} data-testid="copy-razorpay-webhook"><Copy className="w-3 h-3"/></Button>
          </div>
          <div className="mt-4 text-xs text-[#8A7970]">
            To enable Razorpay payment links, add your <code>RAZORPAY_KEY_ID</code> and <code>RAZORPAY_KEY_SECRET</code> to backend/.env then restart. Without them, the bot uses your UPI QR fallback.
          </div>
        </section>

        <section className="bg-[#FDFBF7] rounded-xl border border-[#E7C2A6]/40 p-6">
          <h2 className="font-serif text-2xl mb-3">Rooms</h2>
          {rooms.map(r => (
            <div key={r.id} className="py-3 border-b border-[#E7C2A6]/30 last:border-0 flex justify-between">
              <div>
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-[#8A7970]">{r.description}</div>
              </div>
              <div className="text-right"><div className="font-serif text-lg text-[#B84F3E]">₹{r.weekday_price}</div><div className="text-xs text-[#8A7970]">default</div></div>
            </div>
          ))}
        </section>

        <section className="bg-[#FDFBF7] rounded-xl border border-[#E7C2A6]/40 p-6">
          <h2 className="font-serif text-2xl mb-3">Packages</h2>
          {packages.map(p => (
            <div key={p.id} className="py-3 border-b border-[#E7C2A6]/30 last:border-0">
              <div className="flex justify-between">
                <div className="font-medium">{p.name}</div>
                {p.kind === "food_stay" && <div className="font-serif text-lg text-[#B84F3E]">₹{p.weekday_price}/head</div>}
              </div>
              <div className="text-xs text-[#8A7970] mt-1">{p.description}</div>
              {p.meals.length > 0 && <div className="text-xs mt-1">Meals: {p.meals.join(" · ")}</div>}
            </div>
          ))}
        </section>

        <section className="bg-[#F5F2EB] rounded-xl border border-[#E7C2A6]/40 p-6">
          <h2 className="font-serif text-xl mb-2">Landing Page</h2>
          <p className="text-sm text-[#8A7970] mb-3">Share this URL in your Meta ads (Click-to-WhatsApp not required — but boosts conversion by ~2x):</p>
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#B84F3E] text-sm hover:underline" data-testid="open-landing">
            {window.location.origin}/ <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5}/>
          </a>
        </section>
      </div>
    </div>
  );
}
